import React from 'react';
import style from './UploadDriveIMEIcsv.module.css';

/**
 * IMEIListDisplay
 * ----------------
 * Renders:
 *   (1) The currently selected CSV file name + its size (human readable)
 *   (2) After upload is completed (showResult === true), show the server/API response summary
 *
 * Expectations:
 * - `selectedFile` may temporarily be null (e.g. after clear or after success)
 * - `showResult` guards if upload summary should be visible
 * - Upload summary should be shown even if no selectedFile is present anymore
 *
 * Props:
 *  @param {File|null} selectedFile - The currently selected CSV file or null if cleared
 *  @param {boolean} showResult - Whether to show the upload results panel
 *  @param {object|null} uploadResult - JSON returned by backend after upload, possibly null
 */
const IMEIListDisplay = ({ selectedFile, showResult, uploadResult }) => {
  /**
   * Convert raw byte size into a readable string
   * Examples:
   *   500 => "500 B"
   *   4,500 => "4.39 KB"
   *   6,100,000 => "5.82 MB"
   */
  const humanFileSize = size =>
    size < 1024
      ? `${size} B`
      : size < 1024 * 1024
      ? `${(size / 1024).toFixed(2)} KB`
      : `${(size / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className={style.display_wrapper}>
      {/** ---- FILE INFO PANEL ---- */}
      {selectedFile ? (
        <div className={style.fileinfo}>
          <strong>Selected file:</strong> {selectedFile.name} &nbsp;•&nbsp;{' '}
          {humanFileSize(selectedFile.size)}
        </div>
      ) : (
        /**
         * When file is cleared (after success or reset)
         * show a placeholder instead of crashing or leaving blank
         */
        <div className={style.no_file}>No file selected</div>
      )}

      {/**
       * ---- UPLOAD SUMMARY / RESULT PANEL ----
       * Renders only after upload is finished AND uploadResult is present
       */}
      {showResult && uploadResult && (
        <div className={style.upload_result}>
          <h3>Upload summary</h3>

          <div>Total records: {uploadResult.totalValidRecords ?? 'N/A'}</div>
          <div>
            Successful inserts: {uploadResult.successfulInserts ?? 'N/A'}
          </div>
          <div>Failed inserts: {uploadResult.failedInserts ?? 'N/A'}</div>

          {/**
           * If backend sent any human-readable error messages,
           * list them for user to inspect.
           */}
          {Array.isArray(uploadResult.errorMessages) &&
            uploadResult.errorMessages.length > 0 && (
              <div className={style.error_list}>
                <strong>Messages:</strong>
                <ul>
                  {uploadResult.errorMessages.map((m, idx) => (
                    <li key={idx}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default IMEIListDisplay;
